import { defineComponent as _defineComponent } from 'vue'
import BCXDatePicker from '@/components/molecules/forms/datepicker/BCXDatePicker.vue';
import { computed, ref } from 'vue';
import MeetingSectionItem from '@/components/meeting-scheduler/components/MeetingSectionItem.vue';
import MeetingSection from '@/components/meeting-scheduler/components/MeetingSection.vue';
import BCXButton from '@/components/molecules/BCXButton.vue';
import useI18n from '@/mixins/useI18n';
import useMeetingScheduler from '@/components/meeting-scheduler/composables/useMeetingScheduler';
import dayjs from 'dayjs';
import { PayloadTimeSlot } from '@/models/MeetingScheduler';
import useFormattedDateTime from '@/mixins/useFormattedDateTime';
import MeetingCaption from '@/components/meeting-scheduler/components/MeetingCaption.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'MeetingPicker',
  setup(__props) {

const { t } = useI18n();
const { getFormattedTimeRange, getFormattedDate } = useFormattedDateTime();

const {
  form,
  hasAppointment,
  refreshCalendar,
  selectedDate,
  selectedAppointments,
  pickerYearRange
} = useMeetingScheduler();

const today = new Date();

const suggestion = ref<{
  fullHour: number | null;
  quarterMinute: number | null;
  duration: number | null;
}>({
  fullHour: null,
  quarterMinute: null,
  duration: null
});
const isSuggestionComplete = computed(() => Object.values(suggestion.value).every((v) => v !== null));

const numFullHours = [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];
const numQuarterMinutes = [0, 15, 30, 45];
const numDurations = [15, 30, 45, 60, 90, 120, 180];

const isToday = computed(() => dayjs(selectedDate.value).isSame(dayjs(), 'day'));

const slotStart = (hour: number, minute: number) => dayjs(selectedDate.value)
  .hour(hour)
  .minute(minute)
  .second(0);

// An hour is in the past once its last selectable quarter (hh:45) lies before now.
function isHourDisabled(hour: number): boolean {
  return isToday.value && slotStart(hour, 45).isBefore(dayjs());
}

// A quarter is in the past relative to the currently selected hour.
function isMinuteDisabled(minute: number): boolean {
  if (!isToday.value || suggestion.value.fullHour === null) return false;
  return slotStart(suggestion.value.fullHour, minute).isBefore(dayjs());
}

const isSuggestionInPast = computed(() => (
  (suggestion.value.fullHour !== null && isHourDisabled(suggestion.value.fullHour))
  || (suggestion.value.quarterMinute !== null && isMinuteDisabled(suggestion.value.quarterMinute))
));

const isAddDisabled = computed(() => (
  !isSuggestionComplete.value || isSuggestionInPast.value || form.value.timeSlots.length >= 4
));

function onFullHour(hour: number) {
  if (isHourDisabled(hour)) return;
  suggestion.value.fullHour = hour;
  if (suggestion.value.quarterMinute !== null && isMinuteDisabled(suggestion.value.quarterMinute)) {
    suggestion.value.quarterMinute = null;
  }
}

function onQuarterMinute(minute: number) {
  if (isMinuteDisabled(minute)) return;
  suggestion.value.quarterMinute = minute;
}

function onDuration(duration: number) {
  suggestion.value.duration = duration;
}

function resetSuggestion() {
  suggestion.value = {
    fullHour: null,
    quarterMinute: null,
    duration: null
  };
}

function onAdd() {
  if (!isSuggestionComplete.value || !selectedDate.value) return;

  const start = dayjs(selectedDate.value)
    .hour(suggestion.value.fullHour)
    .minute(suggestion.value.quarterMinute)
    .second(0);

  if (start.isBefore(dayjs())) return;

  const end = start.add(suggestion.value.duration, 'minute');

  const slot: PayloadTimeSlot = {
    start: start.toISOString(),
    end: end.toISOString()
  };

  const isDuplicate = form.value.timeSlots.some((s) => s.start === slot.start && s.end === slot.end);
  if (!isDuplicate) form.value.timeSlots.push(slot);
  resetSuggestion();
}

return { __sfc: true,t, getFormattedTimeRange, getFormattedDate, form, hasAppointment, refreshCalendar, selectedDate, selectedAppointments, pickerYearRange, today, suggestion, isSuggestionComplete, numFullHours, numQuarterMinutes, numDurations, isToday, slotStart, isHourDisabled, isMinuteDisabled, isSuggestionInPast, isAddDisabled, onFullHour, onQuarterMinute, onDuration, resetSuggestion, onAdd, BCXDatePicker, MeetingSectionItem, MeetingSection, BCXButton, MeetingCaption }
}

})